import type { GoalPortfolioSwitchPair, LocalizedString } from "@repo/shared/types";
import { Trash2, TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./switch-pair-dialog.i18n";

/** The holding a switch dialog operates on — matched or unmatchable. */
export type SwitchSource = {
  positionId: string;
  displayName: string;
  /** Frozen holding value. */
  amount: number;
  /** Frozen units — non-null only for matched units products (units mode). */
  units: number | null;
  /** Matched product id (percent-vs-units mode needs it; null = unmatchable). */
  productId: string | null;
};

/** A target the picker offers — pre-filtered by the parent (suitability-
 * available, amount-based only per decision 16, never the source product). */
export type SwitchTargetOption = {
  id: string;
  name: string;
  isin: string | null;
  /** QL-398 — the target's minimum buy (null when none, or not enforced). */
  minBuyAmount: number | null;
};

type SwitchPairDialogProps = {
  /** null = closed. */
  source: SwitchSource | null;
  /** The source's current pairs. */
  pairs: GoalPortfolioSwitchPair[];
  targetOptions: SwitchTargetOption[];
  /** QL-398 — pairs the completion gate flags below their target's minimum
   * (a backstop: data that predates a raised minimum). */
  violatingPairIds?: Set<string>;
  saving: boolean;
  /** Completed instances: list the advised pairs verbatim, no add/edit/remove.
   * Deliberately advice-only — execution status lives on the orders. */
  readOnly?: boolean;
  onClose: () => void;
  /** Removes every pair of this source at once ("Clear switch"). */
  onClearAll: () => void;
  onAddPair: (targetProductId: string, value: number) => void;
  onUpdatePair: (pairId: string, value: number) => void;
  onRemovePair: (pairId: string) => void;
};

/**
 * QL-283 — switch-pair editor for one holding: "move X% (or X units) of this
 * holding into product B". Lists the holding's pairs with inline size editing
 * and removal, plus an add row. The remaining headroom (100 % or the frozen
 * units, minus the other pairs) caps every input live; the server re-checks
 * everything — this dialog only keeps honest input honest. With `readOnly`
 * (completed instances) it becomes a plain viewer of the advised pairs.
 */
export function SwitchPairDialog({
  source,
  pairs,
  targetOptions,
  violatingPairIds,
  saving,
  readOnly = false,
  onClose,
  onClearAll,
  onAddPair,
  onUpdatePair,
  onRemovePair,
}: SwitchPairDialogProps) {
  const t = useT(T);
  const localize = useLocalize();
  const { formatNumber } = useRegionalFormat();
  const [addTargetId, setAddTargetId] = useState<string>("");
  const [addValue, setAddValue] = useState<string>("");
  const [editValues, setEditValues] = useState<Record<string, string>>({});

  // Reset the form whenever the dialog (re)opens for a source or its pairs
  // CONTENT changes under it (a save round-trip refreshed the portfolio
  // payload). Keyed on a content signature, not the array identity — a
  // background refetch returning identical pairs must not wipe in-progress
  // typing. isOpen/positionId cover reopening: the component stays mounted
  // while closed, so identity alone would resurrect stale draft state.
  const isOpen = source !== null;
  const pairsSignature = pairs.map((p) => `${p.id}:${pairValue(p)}`).join("|");
  useEffect(() => {
    setAddTargetId("");
    setAddValue("");
    setEditValues(Object.fromEntries(pairs.map((p) => [p.id, String(pairValue(p))])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, source?.positionId, pairsSignature]);

  if (!source) return null;

  const unitsMode = source.productId !== null && source.units !== null;
  const cap = unitsMode ? (source.units as number) : 100;
  const unitLabel = unitsMode ? t.units.toLowerCase() : "%";
  const allocated = pairs.reduce((sum, p) => sum + pairValue(p), 0);

  const remainingFor = (excludePairId?: string) =>
    cap - pairs.filter((p) => p.id !== excludePairId).reduce((sum, p) => sum + pairValue(p), 0);

  const parseValue = (raw: string): number | null => {
    if (raw.trim() === "") return null;
    const n = Number(raw);
    return Number.isInteger(n) && n >= 1 ? n : null;
  };

  const availableTargets = targetOptions.filter(
    (p) => !pairs.some((pair) => pair.targetProductId === p.id),
  );
  // QL-398 — a switch is its own bank purchase into the target, judged on its
  // own: its estimated proceeds must meet the target's minimum buy. Same
  // arithmetic as the server (`computeSwitchEstimate`): percent of the frozen
  // value, or units priced from the frozen lot — 0 when the lot has no units
  // to price from, as on the server. Returns the shortfall line, or null when
  // the pair passes (or nothing can be judged).
  const estimateFor = (value: number | null): number =>
    value === null || (unitsMode && cap <= 0)
      ? 0
      : Math.round(
          (unitsMode ? (source.amount / cap) * value : source.amount * (value / 100)) * 100,
        ) / 100;
  const belowMinBuyLine = (targetId: string, value: number | null): string | null => {
    const target = targetOptions.find((p) => p.id === targetId);
    const min = target?.minBuyAmount ?? null;
    const estimate = estimateFor(value);
    if (!target || min == null || !(min > 0) || !(estimate > 0) || estimate >= min) return null;
    return t.switchBelowMinBuy({
      estimate: formatNumber(estimate),
      product: target.name,
      min: formatNumber(min),
    });
  };
  const minBuyHintFor = (targetId: string): string | null => {
    const min = targetOptions.find((p) => p.id === targetId)?.minBuyAmount ?? null;
    return min != null && min > 0 ? t.minBuyDialogHint(formatNumber(min)) : null;
  };
  // QL-533 — once the pairs cover the whole holding there is nothing left to
  // switch; offering the add row only led to "between 1 and 0 %". A holding
  // frozen with 0 units has nothing to switch from the start.
  const fullyAllocated = remainingFor() <= 0;
  const nothingToSwitch = cap <= 0 && pairs.length === 0;
  const addParsed = parseValue(addValue);
  const addInvalid = addValue.trim() !== "" && (addParsed === null || addParsed > remainingFor());
  const addBelowMinBuy =
    addTargetId && !addInvalid ? belowMinBuyLine(addTargetId, addParsed) : null;

  return (
    <Dialog open onOpenChange={(open) => !saving && !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t.switchDialogTitle}</DialogTitle>
          <DialogDescription>
            {t.switchDialogDescription(
              source.displayName,
              unitsMode
                ? `${source.units} ${t.units.toLowerCase()} (${formatNumber(source.amount)})`
                : formatNumber(source.amount),
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Read-only (completed): the advised pairs verbatim, then the kept
            remainder and a pointer to where execution actually lives. */}
        {readOnly && (
          <>
            <div className="space-y-1.5">
              {pairs.map((pair) => (
                <div key={pair.id} className="text-sm">
                  ↷ {pairValue(pair)} {unitLabel} →{" "}
                  {localize(pair.targetProductName as LocalizedString) || pair.targetIsin || "—"}
                </div>
              ))}
            </div>
            <div className="space-y-1 border-t pt-3">
              <p className="text-sm text-muted-foreground">
                {t.switchRemainingHint(String(cap - allocated), unitLabel)}
              </p>
              <p className="text-sm text-muted-foreground">{t.switchReadOnlyHint}</p>
            </div>
          </>
        )}

        {/* Existing pairs of this source */}
        {!readOnly && pairs.length > 0 && (
          <div className="space-y-2">
            {pairs.map((pair) => {
              const raw = editValues[pair.id] ?? String(pairValue(pair));
              const parsed = parseValue(raw);
              const invalid = parsed === null || parsed > remainingFor(pair.id);
              const changed = parsed !== null && parsed !== pairValue(pair);
              const belowMinBuy = invalid ? null : belowMinBuyLine(pair.targetProductId, parsed);
              const flagged = violatingPairIds?.has(pair.id) ?? false;
              return (
                <div key={pair.id} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="flex-1 truncate text-sm">
                      {flagged && (
                        <TriangleAlert
                          className="mr-1 inline h-4 w-4 text-destructive"
                          aria-label={t.belowMinBuy}
                        />
                      )}
                      ↷{" "}
                      {localize(pair.targetProductName as LocalizedString) ||
                        pair.targetIsin ||
                        "—"}
                    </span>
                    <Input
                      className="w-24 text-right"
                      type="number"
                      min={1}
                      max={remainingFor(pair.id)}
                      step={1}
                      value={raw}
                      disabled={saving}
                      aria-invalid={invalid || belowMinBuy !== null}
                      onChange={(e) => setEditValues((v) => ({ ...v, [pair.id]: e.target.value }))}
                    />
                    <span className="w-10 text-sm text-muted-foreground">{unitLabel}</span>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={saving || !changed || invalid || belowMinBuy !== null}
                      onClick={() => parsed !== null && onUpdatePair(pair.id, parsed)}
                    >
                      {t.save}
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      tooltip={t.switchRemovePair}
                      disabled={saving}
                      onClick={() => onRemovePair(pair.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  {/* QL-536 — the red border alone left the advisor guessing why
                    Save stayed disabled; name the headroom this pair has. */}
                  {invalid && (
                    <p className="text-sm text-destructive">
                      {t.switchValueInvalid(String(remainingFor(pair.id)), unitLabel)}
                    </p>
                  )}
                  {belowMinBuy && <p className="text-sm text-destructive">{belowMinBuy}</p>}
                </div>
              );
            })}
          </div>
        )}

        {/* Add a pair */}
        {!readOnly && (
          <div className="space-y-2 border-t pt-3">
            <Label>{t.switchAddPairLabel}</Label>
            {fullyAllocated ? (
              <p className="text-sm text-muted-foreground">
                {nothingToSwitch ? t.switchNothingToSwitch : t.switchFullyAllocated}
              </p>
            ) : availableTargets.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t.switchNoEligibleTargets}</p>
            ) : (
              <div className="flex items-center gap-2">
                <SearchableSelect
                  className="flex-1"
                  value={addTargetId}
                  onValueChange={setAddTargetId}
                  disabled={saving}
                  placeholder={t.switchTargetPlaceholder}
                  searchPlaceholder={t.searchPlaceholder}
                  noResultsMessage={t.noProductsMatch}
                  options={availableTargets.map((p) => ({
                    value: p.id,
                    label: p.isin ? `${p.name} (${p.isin})` : p.name,
                  }))}
                />
                <Input
                  className="w-24 text-right"
                  type="number"
                  min={1}
                  max={remainingFor()}
                  step={1}
                  value={addValue}
                  placeholder={String(remainingFor())}
                  disabled={saving}
                  aria-invalid={addInvalid || addBelowMinBuy !== null}
                  onChange={(e) => setAddValue(e.target.value)}
                />
                <span className="w-10 text-sm text-muted-foreground">{unitLabel}</span>
                <Button
                  size="sm"
                  disabled={
                    saving ||
                    !addTargetId ||
                    addParsed === null ||
                    addInvalid ||
                    addBelowMinBuy !== null
                  }
                  onClick={() => addParsed !== null && onAddPair(addTargetId, addParsed)}
                >
                  {t.switchAddPair}
                </Button>
              </div>
            )}
            {!fullyAllocated && (
              <p className="text-sm text-muted-foreground">
                {t.switchRemainingHint(String(cap - allocated), unitLabel)}
              </p>
            )}
            {!fullyAllocated && addInvalid && (
              <p className="text-sm text-destructive">
                {t.switchValueInvalid(String(remainingFor()), unitLabel)}
              </p>
            )}
            {!fullyAllocated && addTargetId && minBuyHintFor(addTargetId) && (
              <p className="text-xs text-muted-foreground">{minBuyHintFor(addTargetId)}</p>
            )}
            {!fullyAllocated && addBelowMinBuy && (
              <p className="text-sm text-destructive">{addBelowMinBuy}</p>
            )}
          </div>
        )}

        <DialogFooter>
          {!readOnly && pairs.length > 0 && (
            <Button variant="destructive" disabled={saving} onClick={onClearAll}>
              {t.switchClearAll}
            </Button>
          )}
          <Button variant="outline" disabled={saving} onClick={onClose}>
            {t.close}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function pairValue(pair: GoalPortfolioSwitchPair): number {
  return pair.switchUnits ?? pair.switchPercent ?? 0;
}
